import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isProjectScope } from '../../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import Button from '../../../generic/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import { useIssueDetailsModals } from '../IssueDetailsModalsContext';

import { FossabotExportTicketModal } from './FossabotExportTicketModal';
import { FossabotFixBadges, FossabotFixOptionCard, FossabotSectionLabel } from './FossabotFixOptionCard';
import { FossabotTicketCreated } from './FossabotTicketCreated';
import { useFossabotFixOptions } from './useFossabotFixOptions';

import styles from './FossabotCreateTicketBox.module.less';

/**
 * The create-ticket remediation path shown below the connection banner when
 * the org has NOT installed the fossabot GitHub app, in project scope only:
 * one radio card per available upgrade, and a Create button that opens the
 * Jira export modal seeded with the selected version. Once the issue has a
 * ticket, the radios are replaced by the TICKET CREATED link (the refetch
 * after export updates the issue in Redux, so that state appears on its own).
 */
export const FossabotCreateTicketBox: VoidFunctionComponent = () => {
  const { options, cve } = useFossabotFixOptions();
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const isTicketed = useSelector(IssuesV2Store.selectors.getIsIssueTicketed);
  const ticket = useSelector(IssuesV2Store.selectors.getFirstJiraTicket);
  const { fossabotExportModal } = useIssueDetailsModals();
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  //  Options are ordered partial then complete, so the last one is the
  //  recommended fix (the complete fix when it exists).
  const recommended = options[options.length - 1];

  /*
    The create-ticket flow acts on a single project's issue, so it only applies
    in project scope. A global or release-group issue spans many projects, where
    a per-project ticket action makes no sense (and one ticketed project would
    wrongly hide Create for the rest), so the box does not render there.
  */
  if (!isProjectScope(scope)) {
    return null;
  }

  //  With no real fix there is nothing to offer; the banner above still renders.
  if (!recommended) {
    return null;
  }

  if (isTicketed && ticket) {
    return (
      <div className={styles.box} data-testid="fossabot-create-ticket-box">
        <div className={styles.topRow}>
          <span className={styles.heading}>Upgrade to {recommended.version}</span>
          <FossabotFixBadges distance={recommended.distance} isComplete={recommended.isComplete} />
        </div>
        <FossabotTicketCreated ticket={ticket} />
      </div>
    );
  }

  return (
    <div className={styles.box} data-testid="fossabot-create-ticket-box">
      <FossabotSectionLabel>CREATE TICKET</FossabotSectionLabel>
      {options.map((option) => (
        <FossabotFixOptionCard
          key={option.version}
          option={option}
          cve={cve}
          value={option.version}
          isSelected={selectedVersion === option.version}
          onSelect={() => setSelectedVersion(option.version)}
        />
      ))}
      <div className={styles.actions}>
        <Button
          type={BUTTON_TYPE.SECONDARY}
          size={BUTTON_SIZE.MEDIUM}
          isDisabled={selectedVersion === null}
          onClick={fossabotExportModal.onOpen}
        >
          Create Ticket
        </Button>
      </div>
      <FossabotExportTicketModal remediationVersion={selectedVersion ?? undefined} />
    </div>
  );
};
